/**
 * IMPORTS
 */

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

const FEATURES = {
  // ACESSO A TELA/MENU (navegação): controlam só se o item aparece no menu
  // lateral e se a tela principal daquele item pode ser aberta. Nunca usar
  // estas para liberar uma ação específica dentro da tela — isso é papel das
  // features de funcionalidade logo abaixo.
  ACESSAR_BACKOFFICE: "acessar_backoffice",
  ACESSAR_ADMIN: "acessar_admin",
  ACESSAR_CLIENTE: "acessar_cliente",
  ACESSAR_REMESSAS: "acessar_remessas",
  ACESSAR_PESQUISAR_REMESSAS: "acessar_pesquisar_remessas",
  ACESSAR_EXPEDICAO: "acessar_expedicao",
  ACESSAR_RECEPCAO: "acessar_recepcao",
  ACESSAR_GERENCIAR_CLIENTE: "acessar_gerenciar_cliente",
  ACESSAR_MODELOS_CLIENTE: "acessar_modelos_cliente",
  ACESSAR_REMESSAS_ANDAMENTO: "acessar_remessas_andamento",
  ACESSAR_HISTORICO_REMESSAS: "acessar_historico_remessas",
  ACESSAR_CONVERSA_CLIENTE: "acessar_conversa_cliente",
  ACESSAR_AGENDA_CLIENTE: "acessar_agenda_cliente",
  ACESSAR_NOTIFICACOES: "acessar_notificacoes",

  // FUNCIONALIDADE: controlam ações/recursos específicos dentro de uma tela
  // já liberada por uma feature de acesso acima.

  // AVISOS GLOBAIS
  EXIBIR_BANNER_MANUTENCAO: "exibir_banner_manutencao",

  // CLIENTE
  VISUALIZAR_CLIENTE: "visualizar_cliente",
  CRIAR_CLIENTE: "criar_cliente",
  EDITAR_CLIENTE: "editar_cliente",
  INATIVAR_CLIENTE: "inativar_cliente",
  GERENCIAR_CREDITOS_CLIENTE: "gerenciar_creditos_cliente",

  // USUARIO CLIENTE
  VISUALIZAR_USUARIO_CLIENTE: "visualizar_usuario_cliente",
  CRIAR_USUARIO_CLIENTE: "criar_usuario_cliente",
  INATIVAR_USUARIO_CLIENTE: "inativar_usuario_cliente",
  EDITAR_USUARIOS_CLIENTE: "editar_usuarios_cliente",

  // CREDITOS
  VISUALIZAR_EXTRATO_CREDITO: "visualizar_extrato_credito",
  VISUALIZAR_COBRANCA_CREDITO: "visualizar_cobranca_credito",
  CRIAR_CREDITO: "criar_credito",
  VISUALIZAR_SALDO_CREDITOS: "visualizar_saldo_creditos",

  // CONVERSA COM A EQUIPE (cliente/subordinado: ler e responder)
  RESPONDER_CONVERSA_CLIENTE: "responder_conversa_cliente",

  // MODELOS TECNICOS DE CLIENTES
  VISUALIZAR_MODELOS_TECNICOS: "visualizar_modelos_tecnicos",
  CRIAR_MODELOS_TECNICOS: "criar_modelos_tecnicos",
  EDITAR_MODELOS_TECNICOS: "editar_modelos_tecnicos",
  INATIVAR_MODELOS_TECNICOS: "inativar_modelos_tecnicos",

  // REMESSAS
  PESQUISAR_REMESSAS: "pesquisar_remessas",
  SOLICITAR_REMESSAS: "solicitar_remessas",
  DEVOLVER_REMESSAS: "devolver_remessas",
  LIBERACAO_MULTIPLAS_REMESSAS_BALCAO: "liberacao_mutiplas_remessas_balcao",
  LIBERACAO_REMESSAS_RECEPCAO: "liberacao_remessas_recepcao",
  LIBERACAO_MULTIPLAS_REMESSAS_RECEPCAO: "liberacao_mutiplas_remessa_recepcao",
  LIBERACAO_REMESSA_BALCAO: "liberacao_remessa_balcao",
  PEGAR_TAREFA_REMESSAS: "pegar_tarefa_remessas",
  MINHAS_TAREFA_REMESSAS: "minhas_tarefa_remessas",
  FINALIZAR_TAREFA_REMESSAS: "finalizar_tarefa_remessas",
  BAIXAR_REMESSAS: "baixar_remessas",
  ADICIONAR_OBSERVACAO_REMESSAS: "adicionar_observacao_remessas",
  EXCLUIR_REMESSAS: "excluir_remessas",
  VISUALIZAR_REMESSAS_ANDAMENTO: "visualizar_remessas_andamento",
  VISUALIZAR_HISTORICO_REMESSAS: "visualizar_historico_remessas",
} as const;

type FeatureKey = (typeof FEATURES)[keyof typeof FEATURES];

function hasFeature(features: readonly string[] | null | undefined, key: FeatureKey): boolean {
  return !!features?.includes(key);
}

function hasAnyFeature(
  features: readonly string[] | null | undefined,
  keys: readonly FeatureKey[]
): boolean {
  return keys.some((key) => hasFeature(features, key));
}

function useFeature(key: FeatureKey): boolean {
  const features = useStoreZustandUserAuth((state) => state.features);

  return hasFeature(features, key);
}

export type { FeatureKey };
/**
 * EXPORTS
 */
export { FEATURES, hasAnyFeature, hasFeature, useFeature };
